// WS-Buyer — the buyer portal Home is a JOURNEY-STAGE ADAPTIVE dashboard.
//
// `home.stage` (application | boarding | active-servicing | delinquent |
// paid-off, classified server-side by PortalJourneyStages.Buyer) drives a
// journey spine plus a SINGLE stage-appropriate next-action that only ever
// offers an action available right now. This kills the old contradiction
// (a static "Turn on autopay" checklist row rendered while the autopay
// section said autopay wasn't available yet) and gives pre-boarding buyers a
// real "your loan is being finalized" state instead of a hollow "You're all
// set." Autopay/bank become live status chips on the loan snapshot; the
// AutopayPanel stays as the management surface for servicing loans only.
//
// Composition rides the shared portal dashboard ENGINE: the ONE composed
// `GET buyer/finance/home` payload stays the page's primary fetch (gating
// via PortalHomeLayout — deliberately NOT split into per-widget fetches),
// while the widget area (loan snapshot, autopay) is a stage-gated
// PortalHomeRecipe whose widgets each render inside a WidgetFrame (they get
// the per-widget error boundary; no `state` — they read the loaded payload).
//
// Portal rules: no React Query — useEffect+useState reads with a cancelled
// guard; writes via useSubmittingMutation.

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  StatusBadge,
  type StatusTone,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency } from "@/design-system/lib/format";
import { AutopayPanel } from "../components/AutopayPanel";
import { PortalRequestsStrip } from "../components/PortalRequestsStrip";
import {
  JourneySpine,
  NextBestActionCard,
  ProgressBar,
  StatTile,
  StatTileRow,
} from "../dashboard/DashboardPrimitives";
import {
  PortalHomeDashboard,
  PortalHomeLayout,
  WidgetFrame,
  type PortalHomeRecipe,
} from "../dashboard/engine";
import { BUYER_SPINE, buyerSpine } from "../dashboard/journey";
import type { BuyerStage } from "../dashboard/journey";
import {
  portalBuyerFinanceApi,
  type BuyerHome,
  type BuyerHomeLoan,
  type BuyerHomeLoanSnapshot,
} from "../api/borrowerFinance";

// ── Widget context + recipe ──────────────────────────────────────────────────
// Every widget renders from the loaded home payload (no per-widget fetch) —
// the frame still gives each one its own error boundary.

interface BuyerHomeCtx {
  home: BuyerHome;
  autopayLoanId: string | null;
  showLoanPicker: boolean;
  autopayRef: RefObject<HTMLDivElement | null>;
  onPickLoan: (loanId: string) => void;
  onAutopayChanged: () => Promise<void>;
  onViewHistory: () => void;
}

const BUYER_HOME_RECIPE: PortalHomeRecipe<BuyerHomeCtx> = {
  widgets: [
    {
      key: "loan-snapshot",
      stages: ["boarding", "active-servicing", "delinquent", "paid-off"],
      hidden: (ctx) => !ctx.home.snapshot,
      render: (ctx) => <LoanSnapshotWidget ctx={ctx} />,
    },
    {
      // Boarding defers autopay coherently — the panel never mounts, the
      // note explains why there's nothing to set up yet.
      key: "autopay-deferred",
      stages: ["boarding"],
      render: () => <AutopayDeferredWidget />,
    },
    {
      // Autopay is manageable only once the loan is actively servicing.
      // Application has no loan; boarding shows the deferred note; paid-off
      // has nothing left to draft.
      key: "autopay",
      stages: ["active-servicing", "delinquent"],
      render: (ctx) => <AutopayWidget ctx={ctx} />,
    },
  ],
};

export function BuyerHomeTab() {
  const toast = useToast();
  const [, setParams] = useSearchParams();
  const [home, setHome] = useState<BuyerHome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [pickedLoanId, setPickedLoanId] = useState<string | null>(null);
  const autopayRef = useRef<HTMLDivElement | null>(null);

  const reload = useCallback(async () => {
    const data = await portalBuyerFinanceApi.home();
    setHome(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void (async () => {
      try {
        const data = await portalBuyerFinanceApi.home();
        if (!cancelled) setHome(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your home page.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  const goToTab = useCallback(
    (tab: string) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set("tab", tab);
          return updated;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Fire-and-forget: viewing the statement / opening payment history counts as
  // the "review" step. Never blocks or surfaces an error.
  const markReviewed = useCallback(() => {
    void portalBuyerFinanceApi
      .markChecklistReviewed()
      .then((updated) => setHome(updated))
      .catch(() => {});
  }, []);

  const pay = useSubmittingMutation(
    (statementId: string) => portalBuyerFinanceApi.payStatement(statementId),
    {
      refresh: reload,
      onSuccess: () =>
        toast.success(
          "Payment started — bank transfers take 3–5 business days to clear. We'll confirm when it clears.",
        ),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Couldn't start that payment."),
    },
  );

  const openStatementPdf = useCallback(
    async (statementId: string) => {
      try {
        const blob = await portalBuyerFinanceApi.statementPdf(statementId);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        toast.error("Couldn't open that statement.");
      }
      markReviewed();
    },
    [toast, markReviewed],
  );

  const scrollToAutopay = useCallback(() => {
    autopayRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  if (!home || error) {
    return <PortalHomeLayout loading error={error} onRetry={retry} testId="buyer-home" />;
  }

  // The backend always classifies a stage; fall back defensively so a partial
  // payload (e.g. an older stub) degrades to the servicing view rather than
  // crashing the spine.
  const stage: BuyerStage = home.stage ?? "active-servicing";
  const position = buyerSpine(stage);

  // The loan the autopay section manages: the current statement's loan, else
  // the snapshot/picked/first of the buyer's linked loans (all in the payload).
  const loans = home.loans ?? [];
  const autopayLoanId =
    home.statement?.loanId ??
    pickedLoanId ??
    home.snapshot?.loanId ??
    loans[0]?.loanId ??
    null;
  const showLoanPicker = home.statement == null && loans.length > 1;

  const ctx: BuyerHomeCtx = {
    home,
    autopayLoanId,
    showLoanPicker,
    autopayRef,
    onPickLoan: setPickedLoanId,
    onAutopayChanged: reload,
    onViewHistory: () => {
      markReviewed();
      goToTab("payments");
    },
  };

  return (
    <PortalHomeLayout stage={stage} testId="buyer-home">
      <JourneySpine
        steps={BUYER_SPINE}
        currentIndex={position.index}
        complete={position.complete}
        testId="buyer-home-journey"
      />

      <NextStep
        home={home}
        payPending={pay.pending}
        onPay={(id) => void pay.submit(id)}
        onViewStatement={(id) => void openStatementPdf(id)}
        onManageAutopay={scrollToAutopay}
        onViewDeals={() => goToTab("deals")}
      />

      {/* Outstanding document requests — a SIBLING of the card above, in the
          same position on every stage. NextStep only knows money and stage, so
          this is the surface that can say whether anything is waiting on the
          buyer; keeping it outside the card is what stops a requests arm from
          displacing the past-due Pay hero. */}
      <PortalRequestsStrip testId="buyer-home-requests" />

      <PortalHomeDashboard
        recipe={BUYER_HOME_RECIPE}
        ctx={ctx}
        stage={stage}
        testId="buyer-home-widgets"
      />
    </PortalHomeLayout>
  );
}

// ── Stage-derived next action ────────────────────────────────────────────────
// The single "what to do next" card. It only ever offers an action available
// RIGHT NOW for the buyer's stage — the pre-boarding branches carry NO pay or
// autopay affordance, so the screen can never contradict itself.

function NextStep({
  home,
  payPending,
  onPay,
  onViewStatement,
  onManageAutopay,
  onViewDeals,
}: {
  home: BuyerHome;
  payPending: boolean;
  onPay: (statementId: string) => void;
  onViewStatement: (statementId: string) => void;
  onManageAutopay: () => void;
  onViewDeals: () => void;
}) {
  const { stage } = home;
  const s = home.statement;

  // Application — pre-boarding: no payment hero, show where the deal stands.
  if (stage === "application") {
    const app = home.application;
    if (!app) {
      return (
        <NextBestActionCard
          heading="Your loan is being finalized"
          description="We're preparing your loan. We'll be in touch — there's nothing to pay yet."
          testId="buyer-home-next-application"
        />
      );
    }
    const multiDeal = app.dealCount > 1;
    const dealLine = `${app.businessName ? `${app.businessName} — ` : ""}Deal ${app.dealNumber} · ${app.stageLabel}`;
    return (
      <NextBestActionCard
        heading={
          multiDeal
            ? `You have ${app.dealCount} deals in progress`
            : "Your loan is being finalized"
        }
        description={
          multiDeal
            ? `Your most recent: ${dealLine}. We'll set up your payment account here as soon as your first loan is boarded — there's nothing to pay yet.`
            : `${dealLine}. We'll set up your payment account here as soon as your loan is boarded — there's nothing to pay yet.`
        }
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={onViewDeals}
            data-testid="buyer-home-view-deal"
          >
            {multiDeal ? "View your deals" : "View your deal"}
          </Button>
        }
        testId="buyer-home-next-application"
      />
    );
  }

  // Boarding — the loan exists but servicing hasn't started.
  if (stage === "boarding") {
    return (
      <NextBestActionCard
        heading="We're setting up your servicing"
        description="We'll email you the moment your first statement is ready. There's nothing to pay yet."
        testId="buyer-home-next-boarding"
      />
    );
  }

  // Paid off — celebratory all-clear. F-14: only when no live payable
  // statement remains — if the classifier says paid-off but a Posted due
  // statement exists, fall through so the due/past-due hero surfaces it
  // instead of "There's nothing more to pay."
  if (stage === "paid-off" && (!s || home.heroState === "all-set")) {
    return (
      <NextBestActionCard
        heading="Paid in full — congratulations!"
        description="Your loan is fully paid off. There's nothing more to pay."
        tone="success"
        testId="buyer-home-next-paid-off"
      />
    );
  }

  // Active servicing / delinquent — the payment next-action, driven by the
  // hero state the backend already computes.
  if (home.heroState === "processing") {
    return (
      <NextBestActionCard
        heading="Payment processing"
        description="Payment processing — bank transfers take 3–5 business days. We'll confirm when it clears."
        actions={
          s ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onViewStatement(s.id)}
              data-testid="buyer-home-view-statement"
            >
              View statement
            </Button>
          ) : undefined
        }
        testId="buyer-home-next-processing"
      />
    );
  }

  if (home.heroState === "autopay-scheduled" && s) {
    return (
      <NextBestActionCard
        heading={`Autopay is on — we'll draft ${formatCurrency(s.amountDue)}${
          home.autopayDraftDate ? ` on ${formatCalendarDate(home.autopayDraftDate)}` : ""
        }. Nothing to pay.`}
        tone="success"
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              loading={payPending}
              disabled={payPending}
              onClick={() => onPay(s.id)}
              className="text-gray-mid"
              data-testid="buyer-home-pay-now-instead"
            >
              Pay now instead
            </Button>
            <Button
              variant="text"
              size="sm"
              onClick={onManageAutopay}
              data-testid="buyer-home-manage-autopay"
            >
              Manage autopay
            </Button>
          </>
        }
        testId="buyer-home-next-autopay-scheduled"
      />
    );
  }

  if (home.heroState === "past-due" && s) {
    return (
      <>
        <NextBestActionCard
          heading={`Your payment of ${formatCurrency(s.amountDue)} was due${
            s.dueDate ? ` ${formatCalendarDate(s.dueDate)}` : ""
          }.`}
          description={
            home.pastDueDays != null
              ? `${home.pastDueDays} day${home.pastDueDays === 1 ? "" : "s"} past due — pay now to bring your loan current.`
              : "Pay now to bring your loan current."
          }
          tone="danger"
          actions={
            <PayActions
              statementId={s.id}
              amountDue={s.amountDue}
              payPending={payPending}
              onPay={onPay}
              onViewStatement={onViewStatement}
            />
          }
          testId="buyer-home-next-past-due"
        />
        {home.pendingFee && (
          <p className="text-small text-gray-mid" data-testid="buyer-home-fee">
            {home.pendingFee.approved ? (
              <>
                A {home.pendingFee.feeType} of {formatCurrency(home.pendingFee.amount)} has
                been assessed and will appear on your next statement.
              </>
            ) : (
              <>
                A {home.pendingFee.feeType} of {formatCurrency(home.pendingFee.amount)} is
                pending review.
              </>
            )}
          </p>
        )}
      </>
    );
  }

  if (home.heroState === "due" && s) {
    return (
      <NextBestActionCard
        heading={`${formatCurrency(s.amountDue)} due${
          s.dueDate ? ` ${formatCalendarDate(s.dueDate)}` : ""
        }`}
        description={`For loan ${s.loanNumber}.`}
        actions={
          <PayActions
            statementId={s.id}
            amountDue={s.amountDue}
            payPending={payPending}
            onPay={onPay}
            onViewStatement={onViewStatement}
          />
        }
        testId="buyer-home-next-due"
      />
    );
  }

  // All-set (nothing owed) — the servicing calm state.
  return (
    <NextBestActionCard
      heading="Your payments are up to date."
      description={
        home.nextExpectedDueDate
          ? `Next statement expected ${formatCalendarDate(home.nextExpectedDueDate)}.`
          : undefined
      }
      tone="success"
      testId="buyer-home-next-all-set"
    />
  );
}

function PayActions({
  statementId,
  amountDue,
  payPending,
  onPay,
  onViewStatement,
}: {
  statementId: string;
  amountDue: number;
  payPending: boolean;
  onPay: (statementId: string) => void;
  onViewStatement: (statementId: string) => void;
}) {
  return (
    <>
      <Button
        variant="primary"
        size="sm"
        loading={payPending}
        disabled={payPending}
        onClick={() => onPay(statementId)}
        data-testid="buyer-home-pay"
      >
        {`Pay ${formatCurrency(amountDue)}`}
      </Button>
      <Button
        variant="text"
        size="sm"
        onClick={() => onViewStatement(statementId)}
        data-testid="buyer-home-view-statement"
      >
        View statement
      </Button>
    </>
  );
}

// ── Loan at a glance ─────────────────────────────────────────────────────────
// Balance / payoff progress / next payment, plus autopay + bank as LIVE STATUS
// CHIPS (not checklist rows) and a short payment-history mini-view. Payoff
// progress renders through the shared ProgressBar (F-47), never a bare "%".
//
// Multi-loan (WS-C): when the buyer holds several loans, a compact chip
// switcher lets them pick which loan the card summarizes. The hero loan keeps
// the rich recent-activity mini-view; a switched-to non-hero loan shows its
// summary figures (from the already-loaded payload — no new fetch) plus a
// "View full ledger" link. Single-loan buyers see exactly the hero view.

function LoanSnapshotWidget({ ctx }: { ctx: BuyerHomeCtx }) {
  const { home, onViewHistory } = ctx;
  const snapshot = home.snapshot;
  const loans = home.loans ?? [];
  const heroLoanId = snapshot?.loanId ?? loans[0]?.loanId ?? null;
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(heroLoanId);

  if (!snapshot) return null;

  const multiLoan = loans.length > 1;
  // Default to the hero loan; if a prior selection no longer resolves (a fresh
  // payload dropped it), fall back to the hero.
  const activeLoanId =
    (selectedLoanId && loans.some((l) => l.loanId === selectedLoanId)
      ? selectedLoanId
      : heroLoanId) ?? snapshot.loanId;
  const isHero = activeLoanId === snapshot.loanId;
  const selected = loans.find((l) => l.loanId === activeLoanId) ?? null;

  return (
    <WidgetFrame title="Your loan" span="full" testId="buyer-home-snapshot">
      {multiLoan && (
        <div
          className="flex flex-wrap items-center gap-space-2"
          role="group"
          aria-label="Choose a loan"
          data-testid="buyer-home-loan-switcher"
        >
          {loans.map((l) => {
            const active = l.loanId === activeLoanId;
            return (
              <Button
                key={l.loanId}
                type="button"
                variant={active ? "secondary" : "tertiary"}
                size="sm"
                aria-pressed={active}
                onClick={() => setSelectedLoanId(l.loanId)}
                data-testid={`buyer-home-loan-chip-${l.loanId}`}
              >
                {l.loanNumber}
              </Button>
            );
          })}
        </div>
      )}

      {isHero ? (
        <HeroLoanSnapshot snapshot={snapshot} home={home} onViewHistory={onViewHistory} />
      ) : (
        <OtherLoanSummary loan={selected} onViewLedger={onViewHistory} />
      )}
    </WidgetFrame>
  );
}

// The hero loan's rich at-a-glance view: figures, payoff progress, live
// autopay/bank chips, and the recent-activity mini-view.
function HeroLoanSnapshot({
  snapshot,
  home,
  onViewHistory,
}: {
  snapshot: BuyerHomeLoanSnapshot;
  home: BuyerHome;
  onViewHistory: () => void;
}) {
  return (
    <>
      <StatTileRow>
        <StatTile
          label="Balance"
          value={formatCurrency(snapshot.outstandingBalance)}
          testId="buyer-home-snapshot-balance"
        />
        <StatTile label="Monthly payment" value={formatCurrency(snapshot.scheduledMonthlyPayment)} />
        <StatTile
          label="Next due"
          value={snapshot.nextDueDate ? formatCalendarDate(snapshot.nextDueDate) : "—"}
        />
      </StatTileRow>

      <ProgressBar
        fraction={snapshot.percentPaid}
        label="Paid off"
        testId="buyer-home-payoff-progress"
      />

      <div className="flex flex-wrap items-center gap-space-2">
        <StatusBadge tone={home.mandateOnFile ? "ok" : "muted"} data-testid="buyer-home-chip-bank">
          {home.mandateOnFile ? `Bank ending ${home.bankLast4 ?? "••••"}` : "No bank connected"}
        </StatusBadge>
        <StatusBadge
          tone={home.autopayEnrolled ? "ok" : "muted"}
          data-testid="buyer-home-chip-autopay"
        >
          {home.autopayEnrolled ? "Autopay on" : "Autopay off"}
        </StatusBadge>
      </div>

      {snapshot.recentActivity.length > 0 && (
        <ul
          className="list-none space-y-space-1 p-0 m-0"
          data-testid="buyer-home-recent-activity"
        >
          {snapshot.recentActivity.map((a) => (
            <li
              key={a.dueDate}
              className="flex items-center justify-between gap-space-3 text-small text-onyx"
            >
              <span className="text-gray-mid">{formatCalendarDate(a.dueDate)}</span>
              <span className="flex items-center gap-space-2">
                {formatCurrency(a.receivedAmount)}
                <StatusBadge tone={activityTone(a.status)}>{a.status}</StatusBadge>
              </span>
            </li>
          ))}
        </ul>
      )}

      <Button
        variant="text"
        size="sm"
        onClick={onViewHistory}
        data-testid="buyer-home-view-history"
      >
        View payment history
      </Button>
    </>
  );
}

// A switched-to non-hero loan: its summary figures (when its ledger was already
// built this request) plus a link into its full ledger. Never triggers a new
// fetch — a loan with no built ledger shows just the link.
function OtherLoanSummary({
  loan,
  onViewLedger,
}: {
  loan: BuyerHomeLoan | null;
  onViewLedger: () => void;
}) {
  if (!loan) return null;
  const hasFigures = loan.outstandingBalance != null;
  return (
    <div className="space-y-space-3" data-testid={`buyer-home-other-${loan.loanId}`}>
      {hasFigures ? (
        <>
          <StatTileRow>
            <StatTile
              label="Balance"
              value={formatCurrency(loan.outstandingBalance ?? 0)}
              testId="buyer-home-other-balance"
            />
            <StatTile
              label="Monthly payment"
              /* BLA Core §2.4 — the combined debit, not the amortization
                 figure; they are equal on a loan without the fee. */
              value={formatCurrency(
                loan.totalMonthlyDebit ?? loan.scheduledMonthlyPayment ?? 0,
              )}
            />
            <StatTile
              label="Next due"
              value={loan.nextDueDate ? formatCalendarDate(loan.nextDueDate) : "—"}
            />
          </StatTileRow>
          {loan.percentPaid != null && (
            <ProgressBar fraction={loan.percentPaid} label="Paid off" />
          )}
        </>
      ) : (
        <p className="text-small text-gray-mid m-0" data-testid="buyer-home-other-note">
          Open this loan&apos;s full ledger to see its balance and schedule.
        </p>
      )}
      <Button
        variant="text"
        size="sm"
        onClick={onViewLedger}
        data-testid="buyer-home-view-ledger"
      >
        View full ledger
      </Button>
    </div>
  );
}

function activityTone(status: string): StatusTone {
  switch (status) {
    case "on-time":
    case "prepaid":
      return "ok";
    case "late":
    case "partial":
    case "nsf":
      return "warn";
    case "missed":
      return "danger";
    default:
      return "neutral";
  }
}

// ── Autopay ──────────────────────────────────────────────────────────────────

function AutopayDeferredWidget() {
  return (
    <WidgetFrame span="full">
      <p className="text-small text-gray-mid m-0" data-testid="buyer-home-autopay-deferred">
        Autopay and online payments turn on automatically once your first
        statement is ready. There&apos;s nothing to set up yet.
      </p>
    </WidgetFrame>
  );
}

function AutopayWidget({ ctx }: { ctx: BuyerHomeCtx }) {
  const { home, autopayLoanId, showLoanPicker } = ctx;
  const loans = home.loans ?? [];
  return (
    <WidgetFrame title="Autopay" span="full" testId="buyer-home-autopay">
      <div ref={ctx.autopayRef} className="space-y-space-3">
        {showLoanPicker && (
          <label className="flex items-center gap-space-2 text-small text-onyx">
            <span className="text-gray-mid">Loan</span>
            <select
              value={autopayLoanId ?? ""}
              onChange={(e) => ctx.onPickLoan(e.target.value)}
              className="h-10 rounded-md border border-border-light bg-white-pure px-space-3 text-small text-onyx focus-visible:outline-none focus-visible:shadow-goldRing"
              data-testid="buyer-home-loan-picker"
            >
              {loans.map((l) => (
                <option key={l.loanId} value={l.loanId}>
                  {l.loanNumber}
                </option>
              ))}
            </select>
          </label>
        )}
        {autopayLoanId && (
          <AutopayPanel loanId={autopayLoanId} onChanged={ctx.onAutopayChanged} />
        )}
      </div>
    </WidgetFrame>
  );
}
